import {Inject, Injectable, NgZone, OnDestroy, Optional, Type} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {HttpClient} from '@angular/common/http';
import {Warehouse} from '../models/entities';
import {BehaviorSubject, concat, defer, EMPTY, Observable, of, ReplaySubject, Subject, throwError, timer} from 'rxjs';
import {DynacrudApi} from '../../core/models/dynacrud';
import {environment} from '../../../environments/environment';
import {DynacrudWeakDataService} from './DynacrudWeakDataService';
import {DynacrudApiWrapper} from '../../core/api/dynacrud-api';
import {catchError, distinctUntilChanged, finalize, map, shareReplay, skip, switchMap, tap, timeout} from 'rxjs/operators';
import {selectAuth} from '../../store/auth/auth.reducers';
import {CROSS_TAB_LOCK, CrossTabLock} from '../utils/cross-tab-lock';


/**
 * The longest a current-warehouse read waits behind a change still on the wire. Past it the read
 * goes out anyway: a PUT stalled on a dropped connection would otherwise hold every read -- the
 * next header, the dashboard's scope -- until the browser gave up on the request, which is minutes
 * of no current warehouse for a wait that exists only to order two answers (MR !217 review).
 */
export const CURRENT_READ_WAIT_MS = 10_000;

/**
 * How long a switch may wait unanswered before it is shown -- and announced -- as pending. Shorter
 * and an ordinary PUT would flash a spinner, and say "pending", on every pick.
 */
export const SWITCH_PENDING_SHOW_MS = 1_000;

/** `pending`, but true only once it has lasted `SWITCH_PENDING_SHOW_MS`. */
export function shownPending(pending: Observable<boolean>): Observable<boolean> {
  return pending.pipe(
    switchMap(p => p ? timer(SWITCH_PENDING_SHOW_MS).pipe(map(() => true)) : of(false)),
    distinctUntilChanged(),
  );
}

/** The channel a tab announces a confirmed switch on, so its siblings follow it. See `WarehouseService.channel`. */
export const WAREHOUSE_CHANNEL = 'warehouse-current';

/** What a tab announces after the server confirmed a switch. */
interface WarehouseSwitchMessage {
  type: 'switched';
  /** The user the switch belongs to: the current warehouse is per USER on the server. */
  userId: number;
  warehouse: Warehouse | null;
  /**
   * When the server confirmed it, taken while the switch held the cross-tab lock: the lock
   * serialises this browser's confirmations, so stamps increase in the order the server applied
   * them. Absent from a tab still on the previous build. See `WarehouseService.lastSeenStamp`.
   */
  stamp?: number;
}

/** One change PUT: on the wire, or waiting behind the one that is. See `WarehouseService.wireChange`. */
interface ChangeSlot {
  id: number | null;
  /** The `currentEpoch` this change set -- its answer writes only while that is still current. */
  epoch: number;
  /** The `identityGeneration` it was made under. */
  generation: number;
  result: ReplaySubject<DynacrudApi<Warehouse>>;
  /** Completed when the PUT settles (or the change is dropped). What a read waits on. */
  done: Subject<never>;
  /** Set once the server confirmed this change. Read when the last change in line settles. */
  ok?: boolean;
  /** Set when the server confirmed it, under the lock. See `WarehouseSwitchMessage.stamp`. */
  stamp?: number;
}

/**
 * How the switches in line ended, once nothing is left in line: the last one went through, the last
 * one failed, or an identity change dropped them. See `WarehouseService.switchEnded`.
 */
export type WarehouseSwitchEnd = 'done' | 'failed' | 'dropped';

/** Same scope: both known and the same warehouse (or both none). `undefined` -- not known -- is never the same. */
function sameWarehouse(a: Warehouse | null | undefined, b: Warehouse | null | undefined): boolean {
  return a !== undefined && b !== undefined && (a === b || (a != null && b != null && a.id === b.id));
}

@Injectable({
  providedIn: 'root'
})
export class WarehouseService extends DynacrudWeakDataService<Warehouse> implements OnDestroy {

  /**
   * THREE STATES, AND THE FIRST TWO ARE NOT THE SAME THING.
   *
   * `undefined` means NOT KNOWN YET -- the seed every new subscriber is replayed before
   * `/v2/warehouses/current` has answered. `null` means genuinely NO warehouse, i.e. "all
   * warehouses", which is a real choice a user can make. A warehouse means that warehouse.
   *
   * Collapsing the first two is not cosmetic: a consumer reading `!w` as "no warehouse" acts on the
   * seed, does a full round of work for a scope nobody has chosen, and is superseded moments later
   * when the real warehouse arrives. That is exactly what the shop dashboard did -- two
   * `statusCount` requests and two list searches on every reload, with the list visibly repainting
   * from the unscoped rows to the scoped ones. A consumer that cannot act until the scope is known
   * must filter `undefined` out, not test truthiness.
   */
  private warehouseChangeSub: BehaviorSubject<Warehouse | null | undefined> = new BehaviorSubject<Warehouse | null | undefined>(undefined);

  /**
   * Deduped BY VALUE, because asking again is not a change.
   *
   * `getCurrentWarehouse()` is called several times per page load -- `SelectWarehouseComponent`
   * fires it on every route-conf emission, and boot produces more than one -- and each call pushes a
   * FRESH object parsed from its own response. Consumers dedupe with a reference-based
   * `distinctUntilChanged`, so the identical warehouse arriving twice read as two changes and each
   * one re-counted statuses and re-searched its list: the duplicate `statusCount` and duplicate
   * search seen on all three order lists, and the flicker with them. Identity cannot tell "the
   * warehouse changed" from "we asked again"; the id can.
   *
   * `a === b` FIRST, and it is the clause that matters: it keeps `undefined` (not known yet) and
   * `null` (no warehouse) apart, which `a?.id === b?.id` would collapse since both answer
   * `undefined`. Consumers wait for the stream to leave `undefined`, so swallowing that step would
   * strand them forever -- exactly the failure `getCurrentWarehouse`'s error path exists to prevent.
   */
  public readonly warehouseChange: Observable<Warehouse | null | undefined> = this.warehouseChangeSub
    .asObservable()
    .pipe(distinctUntilChanged((a, b) => a === b || sameWarehouse(a, b)));

  /**
   * The current-warehouse request while it is in flight, shared by every caller.
   *
   * `SelectWarehouseComponent` asks on every route-conf emission and boot produces several, so this
   * went out three times per load with two cancelled. They OVERLAP, which is exactly what sharing
   * collapses. Scoped to the flight rather than cached (see `finalize` below), so nothing here
   * changes how fresh the answer is.
   */
  private currentInFlight?: Observable<DynacrudApi<Warehouse>>;
  /**
   * Bumped by every CHANGE, so an answer about the warehouse we have left cannot land on top of it.
   *
   * `shareReplay({refCount: false})` above is what makes this necessary. It keeps the in-flight GET
   * subscribed after its last caller has gone -- deliberately, so paid-for work is not thrown away
   * -- which also means the request now always COMPLETES and always runs its `tap`. Pick a
   * different warehouse while one is open, let the PUT answer first, and the older GET then pushes
   * the PRE-CHANGE warehouse into `warehouseChangeSub`: the status chips, the dashboard header's
   * own search and the cart's completed-orders list all re-scope back to the warehouse the user
   * just left. Before the request was shared it at least died with its subscriber.
   *
   * Read as a stamp rather than a count: a GET captures the value in force when it was CREATED and
   * writes only while that value is still current. A change supersedes every GET older than
   * itself, and two changes racing each other resolve last-one-wins the same way -- the earlier
   * PUT's answer is no more current than the earlier GET's.
   */
  private currentEpoch = 0;


  /**
   * Bumped by every IDENTITY change, and read by a change PUT at the moment it is actually SENT.
   *
   * Not the same stamp as `currentEpoch`: a later change bumps that one too, and a PUT queued behind
   * an earlier one must still go out -- that ordering is the whole point of the queue. An identity
   * change is different. A PUT waiting behind another is subscribed only when that one settles, and
   * the interceptor attaches whatever bearer is in force THEN: after a cross-tab switch that is the
   * incoming user's, so the outgoing user's pick would set the INCOMING user's current warehouse on
   * the server (MR !217 review). A PUT already on the wire went out under its own user and is left
   * alone; one still queued is dropped.
   */
  private identityGeneration = 0;

  /**
   * The change PUT on the wire, and the ONE change waiting behind it.
   *
   * STRICTLY one PUT at a time, with no time limit on the wait. Two PUTs on the wire together can be
   * applied by the server in either order, leaving it on the EARLIER pick while every answer here
   * describes the later one -- and no client-side repair can undo that after the fact: re-sending the
   * latest pick when an earlier one settled late (MR !222, first cut) re-sent picks the server had
   * REFUSED, could not tell a failed late PUT from an applied one, and left the select describing a
   * different warehouse from the server. A stalled PUT therefore holds the next switch until the
   * browser or the gateway ends it -- in EVERY tab of this user, since it holds the cross-tab lock
   * (see `send`); closing the stalled tab releases it. Reads are still bounded
   * (`CURRENT_READ_WAIT_MS`), so a stall delays a switch and never strands the header.
   *
   * Picks made while one is on the wire COLLAPSE into `queuedChange`: only the latest is sent, since
   * the user has moved past the others. A superseded caller's observable completes without a value,
   * like a change dropped by an identity change.
   *
   * Each carries the identity it was made under (see `identityGeneration`). A PUT on the wire for an
   * OUTGOING identity does not hold the incoming user's changes: it moves the outgoing user's current
   * warehouse, so there is nothing to order against.
   */
  private wireChange?: ChangeSlot;
  private queuedChange?: ChangeSlot;

  /**
   * The latest change the server confirmed and the siblings have not yet been told about. Held until
   * the last change in line settles (see `settle`), so a sibling follows the warehouse the server
   * ends up on rather than every pick on the way to it.
   */
  private unannounced?: { slot: ChangeSlot; warehouse: Warehouse | null };

  /**
   * The newest stamp this tab knows of: its own confirmed switches and every announcement raise it.
   * An announcement stamped at or below it is older than something already in force here, and is
   * ignored -- lock grant and `postMessage` delivery are not ordered with each other, so an earlier
   * switch's message can arrive after a later one has settled.
   */
  private lastSeenStamp = 0;

  /**
   * The change PUT still in flight, which a later read must WAIT for.
   *
   * The epoch stamp keeps an OLDER read from landing on top of a change; it says nothing about a
   * read made AFTER the change was sent, which carries the change's own epoch and so writes. Sent
   * while the PUT is still on the wire, the server may answer it before the PUT has committed --
   * and that is the ordinary case, not an exotic one: `SelectWarehouseComponent` flushes a held pick
   * from `ngOnDestroy` on a route change, and the header that replaces it reads the current
   * warehouse straight away. The new header then showed the warehouse the user had left while the
   * server held the one they picked (MR !217 review). Chaining the read behind the PUT (its outcome
   * ignored -- a failed change still has to be followed by a read of what is really current)
   * makes "a read sent after a change" mean "a read answered after it". The wait is bounded by
   * `CURRENT_READ_WAIT_MS`, so a stalled PUT delays a read rather than stranding it -- and a read
   * that gives up waiting is MARKED, because its answer cannot describe the change it went out
   * beside (see `besideChange`).
   */
  private get pendingChange(): Observable<never> | undefined {
    // DERIVED from the queue rather than kept beside it, so the two cannot disagree (MR !222 review):
    // the last change in line, which with one PUT at a time settles only after every earlier one has.
    return this.lastChangeInLine()?.done.asObservable();
  }

  /**
   * The change a read has to wait for: the queued one, else the one on the wire -- unless that one
   * went out for an identity that has since gone, which moves the OUTGOING user's current warehouse
   * and so has nothing to order a read for the incoming user against (see `forgetCurrent`).
   */
  private lastChangeInLine(): ChangeSlot | undefined {
    if (this.queuedChange) {
      return this.queuedChange;
    }
    return this.wireChange?.generation === this.identityGeneration ? this.wireChange : undefined;
  }

  /**
   * Answers to reads that went out while a change was still in line -- the ones that stopped waiting
   * at `CURRENT_READ_WAIT_MS`. Such an answer was given while the server had NOT yet applied the
   * change (the PUT stalled on the wire, or was still queued behind one that did), so it names the
   * warehouse the user has just left. It carries the change's own epoch, so the epoch stamp lets it
   * through; written, it put the select back on that warehouse while the change was about to land,
   * and the header then skipped a re-pick of it as "already in force", leaving the user unable to
   * switch back (MR !222 review). The service keeps it off the stream; a caller that writes what it
   * reads asks `answeredBesideChange()`. Readers that only need SOME answer -- the stock search's
   * column, which already falls back to none -- still get one.
   */
  private readonly besideChange = new WeakSet<object>();

  /** Whether `answer` came from a read that went out beside a change still in line. See `besideChange`. */
  answeredBesideChange(answer: unknown): boolean {
    return typeof answer === 'object' && answer !== null && this.besideChange.has(answer);
  }

  /**
   * Whether a switch has been asked for and not yet answered. One PUT at a time, with no limit on how
   * long the next waits (see `wireChange`), so a PUT stalled on a dropped connection holds a switch the
   * select already shows: this is how the select says so instead of looking done (MR !222 review).
   */
  private readonly switchPendingSub = new BehaviorSubject<boolean>(false);
  readonly switchPending: Observable<boolean> = this.switchPendingSub.pipe(distinctUntilChanged());

  /**
   * `switchPending`, true only once it has lasted `SWITCH_PENDING_SHOW_MS`: what the select's spinner
   * and the announcer's wait both read. ONE timer for the page: computed per header, a header rebuilt
   * by a navigation mid-switch restarted its own timer and hid the spinner for a second while the
   * announcer's wait went on. `refCount: false` keeps the timer running across that rebuild.
   */
  readonly switchPendingShown: Observable<boolean> = shownPending(this.switchPending)
    .pipe(shareReplay({ bufferSize: 1, refCount: false }));

  /**
   * How the switches in line ended, emitted once nothing is left in line and BEFORE `switchPending`
   * drops. The announcement of a stalled switch reads it rather than guessing from its own state: a
   * header kept a "last switch failed" flag of its own, which a later pick in line could not clear,
   * which a header rebuilt by a navigation never saw set, and which an identity change -- completing
   * the dropped change with no value -- never set at all. Each announced "Warehouse changed" for a
   * switch that had not happened, or nothing for one that had (MR !222 review, round 6).
   */
  private readonly switchEndedSub = new Subject<WarehouseSwitchEnd>();
  readonly switchEnded: Observable<WarehouseSwitchEnd> = this.switchEndedSub.asObservable();

  /**
   * A switch another TAB of the same user made, announced on `channel` once the server confirmed it.
   * Already in this service's stream by the time it emits; `SelectWarehouseComponent` follows it so its
   * select stops naming the warehouse this tab last saw. Without it the only way to notice was to
   * re-read on every window focus (MR !222 review).
   */
  private readonly externalSwitchSub = new Subject<Warehouse | null>();
  readonly externalSwitch: Observable<Warehouse | null> = this.externalSwitchSub.asObservable();

  /**
   * Same-browser tabs tell each other about a confirmed switch here. A switch made on another DEVICE
   * never arrives this way; the header still re-reads on focus for that, throttled. `null` where the
   * browser has no BroadcastChannel.
   */
  private readonly channel: BroadcastChannel | null = typeof BroadcastChannel === 'undefined'
    ? null
    : new BroadcastChannel(WAREHOUSE_CHANNEL);

  /** The signed-in user, for stamping and filtering `channel` messages. 0 when nobody is. */
  private userId = 0;

  /**
   * `Store` is `@Optional()` only so the many specs that build this service with no store keep
   * working; at runtime `AppModule` always provides one.
   */
  constructor(private http: HttpClient, dynacrudApiWrapper: DynacrudApiWrapper, private zone: NgZone,
              @Inject(CROSS_TAB_LOCK) private lock: CrossTabLock,
              @Optional() store?: Store | null) {
    super('warehouses', WarehouseService, dynacrudApiWrapper);
    if (this.channel) {
      // Not zone-patched: re-enter the zone, or the select would follow the switch with no change
      // detection to show it (the same trap as `UserSettingStore`'s channel).
      this.channel.onmessage = (event: MessageEvent) => this.zone.run(() => this.onChannelMessage(event.data));
    }
    // The current warehouse is per USER on the server, and this service is root-scoped, so it has to
    // notice an identity change itself: wired from one component, it only ran while the shop header
    // happened to be mounted, and a switch made while the tab was under /app/warehouse left the
    // outgoing user's warehouse in the stream for the incoming one (MR !217 review). `skip(1)`: the
    // first emission is the identity this service was built under, not a change.
    store?.pipe(
      select(selectAuth),
      map(auth => auth?.userId ?? 0),
      tap(userId => this.userId = userId),
      distinctUntilChanged(),
      skip(1),
    ).subscribe(userId => this.onIdentityChange(userId));
  }

  ngOnDestroy(): void {
    this.channel?.close();
  }

  getType(): Type<Warehouse> | string {
    return 'warehouse';
  }

  getCurrentWarehouse(): Observable<DynacrudApi<Warehouse>> {
    if (!this.currentInFlight) {
      // Captured at CREATION, which is the moment this answer becomes "current" -- see `currentEpoch`.
      const epoch = this.currentEpoch;
      // Decided when the GET is SENT, i.e. after the wait below: still a change in line then means the
      // wait gave up and this answer cannot describe it (see `besideChange`).
      let beside = false;
      const read = defer(() => {
        beside = !!this.lastChangeInLine();
        return this.http.get<DynacrudApi<Warehouse>>(`${environment.apiUrl}/v2/warehouses/current`);
      }).pipe(tap(answer => {
        if (beside && answer) {
          this.besideChange.add(answer);
        }
      }));
      // Behind a change still on the wire, never beside it -- see `pendingChange`.
      const waitForChange = this.pendingChange?.pipe(timeout({ first: CURRENT_READ_WAIT_MS, with: () => EMPTY }));
      const flight: Observable<DynacrudApi<Warehouse>> = (waitForChange ? concat(waitForChange, read) : read).pipe(
        // `?? null`, and it is the whole contract above rather than defensive noise: a SUCCESS must
        // never re-emit `undefined`, because that is this stream's word for "not known yet" and
        // consumers filter it out (`DashboardHeaderComponentsComponent` does, by name). An envelope
        // whose `data` is absent rather than an explicit `null` -- which is how "no current
        // warehouse" can legitimately arrive, and why `SelectWarehouseComponent` has always guarded
        // `data && data.data` -- would strand every one of them for the life of the page: no status
        // chips, no search, and nothing on screen saying why. `null` is the honest reading of an
        // answered request with no warehouse in it, and it is the same value the error path emits.
        //
        // A read that answered BESIDE a change is kept off the stream (see `besideChange`) -- except
        // while the stream still knows NOTHING. The first load with a pick made before the first read
        // landed and a PUT that then stalls left every consumer on "not known yet", i.e. no status
        // chips and no rows, until the browser ended the PUT (MR !222 review, round 5). The warehouse
        // being left is a scope; nothing is none. The PUT, when it lands, writes the one picked.
        tap(w => {
          if (this.isCurrent(epoch) && (!beside || this.warehouseChangeSub.value === undefined)) {
            this.warehouseChangeSub.next(w?.data ?? null);
          }
        }),
        // This request is the ONLY thing that moves the stream off "not known yet", so a failure must
        // still resolve it or every consumer waiting for the scope waits forever -- a dashboard with
        // no status chips and no rows, and nothing on screen saying why. `null` is the honest answer:
        // we asked and have no current warehouse, which is the same unscoped view the header used to
        // fall back to. The caller still sees the error.
        //
        // Superseded by a change, it still must not answer: `null` here would tell every consumer
        // there is no warehouse moments after the user picked one. The CALLER is still given the
        // error either way -- `SelectWarehouseComponent`'s own change handler re-reads on a failed
        // PUT, and that re-read is a fresh request at the current epoch, so it is free to write.
        //
        // Only while NOTHING is known, though. A RE-read that fails -- the shop header asks again on
        // every window focus, and a laptop waking from sleep focuses before its network is back --
        // has learnt nothing new: `null` there re-scoped every list to "all warehouses" and dropped
        // the filter the user was working under (MR !222 review). The last answer stays.
        catchError(err => {
          // Beside a change or not: with nothing known, `null` is the only answer that unblocks.
          if (this.isCurrent(epoch) && this.warehouseChangeSub.value === undefined) {
            this.warehouseChangeSub.next(null);
          }
          return throwError(() => err);
        }),
        // Released on complete OR error, so this shares a FLIGHT and is not a cache: the next caller
        // after it lands asks again. That keeps the refresh-on-navigation the app has today, and
        // with it the case where another TAB changed the current warehouse.
        //
        // Only if the field still holds THIS flight: a change, or a followed announcement, drops the
        // field itself, so a later reader can already have put a FRESH flight there by the time
        // this one finishes. Clearing it then evicts that one and costs the next caller a duplicate
        // request -- the exact waste sharing exists to remove.
        finalize(() => {
          if (this.currentInFlight === flight) {
            this.currentInFlight = undefined;
          }
        }),
        // `refCount: false` is the load-bearing half. A caller that goes away must not take the
        // request with it -- the two cancelled requests in the network panel were work already paid
        // for and thrown away, and the component that replaced them simply asked again.
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.currentInFlight = flight;
    }
    return this.currentInFlight;
  }

  /**
   * `?? null` for the same reason as `getCurrentWarehouse` above, and this is the call where the
   * empty answer is the EXPECTED one: picking "all warehouses" PUTs `null`, so the response
   * legitimately carries no warehouse. Emitting `undefined` there would tell every consumer the
   * scope had become unknown at the exact moment the user chose it.
   */
  changeCurrentWarehouse(currWarehouseId: number | null): Observable<DynacrudApi<Warehouse>> {
    // `defer`, so the epoch moves when the change is actually MADE rather than when the observable
    // is built. The PUT is cold and every caller in the tree subscribes to it exactly once, so the
    // two coincide today -- but an epoch stamped at build time would be spent by a caller that
    // composed the request and never subscribed, superseding a GET on the strength of a change
    // nobody asked the server for.
    return defer(() => {
      // Supersede every answer older than this change (see `currentEpoch`), and drop the shared
      // flight so the next reader asks again rather than replaying a pre-change answer -- the
      // observable itself stays subscribed, which is exactly why the stamp and not this line is
      // what silences it.
      const epoch = ++this.currentEpoch;
      this.currentInFlight = undefined;
      // The SERVICE subscribes to the PUT, and the caller to `result`: a caller unsubscribing cannot
      // cancel a switch the select has already shown, and a read made meanwhile keeps waiting for it
      // (pinned in the spec: "a read still waits for a change whose caller has already
      // unsubscribed").
      const result = new ReplaySubject<DynacrudApi<Warehouse>>(1);
      const generation = this.identityGeneration;
      const wire = this.wireChange;
      if (!wire || wire.generation !== generation) {
        this.send({ id: currWarehouseId, epoch, generation, result, done: new Subject<never>() });
      } else if (currWarehouseId === wire.id) {
        // Back on the pick already on the wire: the PUT out there IS this change. Queuing it again
        // sent the same PUT twice once the first settled, moving the epoch, dropping the shared read
        // and re-scoping the dashboard for no change (MR !222 review, round 4). The wire's answer
        // becomes the latest pick's -- it takes this epoch, so it still writes the stream -- and the
        // caller is answered by it. A pick queued behind it is dropped, and a read already waiting on
        // that one's `done` waits for the wire instead.
        wire.epoch = epoch;
        const queued = this.queuedChange;
        this.queuedChange = undefined;
        if (queued) {
          queued.result.complete();
          wire.done.subscribe({ complete: () => queued.done.complete() });
        }
        wire.result.subscribe(result);
      } else if (this.queuedChange) {
        // Collapse: the queued pick was never sent and the user has moved past it. Its slot (and the
        // `done` a read may already be waiting on) is reused for the latest pick.
        const queued = this.queuedChange;
        queued.result.complete();
        queued.id = currWarehouseId;
        queued.epoch = epoch;
        queued.result = result;
      } else {
        this.queuedChange = { id: currWarehouseId, epoch, generation, result, done: new Subject<never>() };
      }
      this.syncSwitchPending();
      return result.asObservable();
    });
  }

  /**
   * Put `slot` on the wire, under the cross-tab lock `g15-warehouse-current:<userId>`, held until its
   * PUT settles. With the lock, this browser's tabs never have two change PUTs on the wire together,
   * so the server applies them in lock order and their answers arrive in that order: a second tab
   * switching meanwhile waits (pending), instead of racing and then guessing which switch stuck.
   * Taken per PUT, never for the whole queue, so a tab that keeps picking cannot starve the others.
   */
  private send(slot: ChangeSlot): void {
    this.wireChange = slot;
    void this.lock(`g15-warehouse-current:${this.userId}`, () => new Promise<void>(release => {
      if (slot.generation !== this.identityGeneration) {
        // Made for an identity that has gone while it waited: never sent under the incoming user's
        // bearer, as a queued change is dropped (see `forgetCurrent`, which reported it dropped).
        slot.result.complete();
        if (this.wireChange === slot) {
          this.wireChange = undefined;
        }
        this.syncSwitchPending();
        slot.done.complete();
        release();
        return;
      }
      const queued = this.queuedChange;
      if (queued) {
        // Picked while this one waited: send the latest, as a queue collapses. The waiting caller
        // completes without a value, like any superseded pick; a read waiting on the queued `done`
        // is released with this one's.
        this.queuedChange = undefined;
        slot.result.complete();
        slot.id = queued.id;
        slot.epoch = queued.epoch;
        slot.result = queued.result;
        slot.done.subscribe({ complete: () => queued.done.complete() });
      }
      this.http.put<DynacrudApi<Warehouse>>(`${environment.apiUrl}/v2/warehouses/current`, slot.id).pipe(
        tap(w => {
          slot.ok = true;
          // Under the lock, so stamps increase in the order the server applied the switches. `max`,
          // so one tab's own stamps still increase when the clock stands still or steps back.
          slot.stamp = Math.max(Date.now(), this.lastSeenStamp + 1);
          this.lastSeenStamp = slot.stamp;
          if (this.isCurrent(slot.epoch)) {
            this.warehouseChangeSub.next(w?.data ?? null);
          }
          // Announced from `settle`, once nothing follows it -- see `unannounced`. Not for an outgoing
          // identity's PUT: that moved THEIR warehouse, and `forgetCurrent` has already cleared the field.
          if (slot.generation === this.identityGeneration) {
            this.unannounced = { slot, warehouse: w?.data ?? null };
          }
        }),
        // Runs once, when the request itself settles, success or failure -- a failed change still
        // releases the next one, and is never sent again.
        finalize(() => {
          // `finally`: a lock left held would hang every switch in every tab of this user.
          try {
            this.settle(slot);
          } finally {
            release();
          }
        }),
      ).subscribe(slot.result);
    }));
  }

  private settle(slot: ChangeSlot): void {
    const done = slot.done;
    if (this.wireChange === slot) {
      this.wireChange = undefined;
      const next = this.queuedChange;
      this.queuedChange = undefined;
      if (next) {
        // Always this identity's: a change is queued only behind one of the same generation, and
        // `forgetCurrent` -- the one place the generation moves -- drops whatever is queued.
        this.send(next);
      } else {
        if (slot.generation === this.identityGeneration) {
          // The last change in line, so how IT ended is how the switch ended: an earlier failure the
          // user has picked past is not the outcome. An outgoing identity's PUT ends nothing -- the
          // identity change already reported its switch dropped (see `forgetCurrent`).
          this.switchEndedSub.next(slot.ok ? 'done' : 'failed');
        }
        const unannounced = this.unannounced;
        this.unannounced = undefined;
        if (unannounced && !slot.ok) {
          // The confirmed change's own tap skipped the stream: the pick queued behind it had already
          // moved the epoch. With that pick failed, this tab's stream still named the warehouse from
          // BEFORE both while the siblings were told the confirmed one -- and the header, its
          // in-force id cleared by the failure, copied that stale value in and then skipped a
          // re-pick of it (MR !222 review, round 7). The server holds the confirmed one, unless a
          // sibling switched after it -- whose announcement this tab ignored while its line was busy;
          // the header's resync read after the failure lands on that, and the stamp below is older
          // than the sibling's, so the siblings drop this announcement.
          this.warehouseChangeSub.next(unannounced.warehouse);
        }
        if (unannounced) {
          // The last change in line has settled: tell the siblings what the server ended up holding.
          // Announcing every confirmed PUT made a sibling re-scope to a pick already superseded by the
          // one queued behind it, then again to that one (MR !222 review, round 3). A failed final
          // change still announces the earlier confirmed one, which is what the server now holds.
          this.announce(unannounced.slot.generation, unannounced.warehouse, unannounced.slot.stamp);
        }
      }
    }
    this.syncSwitchPending();
    done.complete();
  }

  /**
   * Tell this user's other tabs the server now holds `warehouse`. Only for a change made under the
   * identity still signed in: a PUT that went out for an outgoing user moved THEIR warehouse, and the
   * tabs listening are the incoming user's.
   */
  private announce(generation: number, warehouse: Warehouse | null, stamp?: number): void {
    if (!this.channel || !this.userId || generation !== this.identityGeneration) {
      return;
    }
    const message: WarehouseSwitchMessage = { type: 'switched', userId: this.userId, warehouse, stamp };
    try {
      this.channel.postMessage(message);
    } catch {
      // A closed channel or an uncloneable payload: the siblings still re-read on focus.
    }
  }

  /**
   * Another tab of this user switched. Follow it -- unless it is older than something already known
   * here (see `lastSeenStamp`), or THIS tab has a change in line: that change waits for, or holds,
   * the cross-tab lock, so the server applies it after the announced one and following would
   * describe a warehouse about to be replaced. Supersede every read already out (it was asked before
   * the switch) and drop the shared flight, exactly as a local change does (see `currentEpoch`).
   */
  private onChannelMessage(data: unknown): void {
    const message = data as Partial<WarehouseSwitchMessage> | null;
    if (!message || message.type !== 'switched' || !this.userId || message.userId !== this.userId) {
      return;
    }
    // Whatever arrives becomes the scope of every list, so it is checked rather than cast: no
    // warehouse, or an object with a numeric id. Anything else is ignored -- the focus re-read still
    // catches a real switch (MR !222 review, round 5).
    const warehouse = message.warehouse;
    if (warehouse !== null && !(typeof warehouse === 'object' && typeof (warehouse as Warehouse | undefined)?.id === 'number')) {
      return;
    }
    // No stamp: a tab still on the previous build. Followed by the rules below, unordered.
    if (typeof message.stamp === 'number') {
      if (message.stamp <= this.lastSeenStamp) {
        return;
      }
      this.lastSeenStamp = message.stamp;
    }
    if (this.lastChangeInLine()) {
      return;
    }
    this.follow(warehouse);
  }

  /** Put `warehouse` in force here because the server holds it, not because this tab asked for it. */
  private follow(warehouse: Warehouse | null): void {
    // Already in force here: nothing to follow. Moving the epoch would drop a read already out and
    // the shared flight, and the header would drop a walk held on its select, for no change
    // (MR !222 review, round 6).
    if (sameWarehouse(this.warehouseChangeSub.value, warehouse)) {
      return;
    }
    this.currentEpoch++;
    this.currentInFlight = undefined;
    this.warehouseChangeSub.next(warehouse);
    this.externalSwitchSub.next(warehouse);
  }

  private syncSwitchPending(): void {
    this.switchPendingSub.next(!!this.lastChangeInLine());
  }

  /**
   * The identity changed. The current warehouse is per USER on the server, so everything known here
   * describes somebody else's: supersede every read made for the previous identity (the shared flight
   * included, exactly as a change does -- see `currentEpoch`), drop any change PUT still queued (see
   * `identityGeneration`), and put the stream back to "not known yet", so no consumer scopes the
   * incoming user's lists to the outgoing user's warehouse.
   *
   * Then, if anything had read the warehouse and somebody is signed in, ask again for the incoming
   * user: consumers wait for the stream to leave `undefined`, and a navigation to the URL the tab is
   * already on re-reads nothing. A logout (`userId` 0) asks nothing -- there is no current warehouse
   * to read.
   */
  private onIdentityChange(userId: number): void {
    const wasKnown = this.warehouseChangeSub.value !== undefined;
    this.forgetCurrent();
    if (wasKnown && userId) {
      // Errors are already handled for the stream (`null`) and toasted by the interceptor.
      this.getCurrentWarehouse().subscribe({ error: () => undefined });
    }
  }

  /**
   * Forget everything known about the current warehouse. See `onIdentityChange` -- its only caller,
   * and private for that reason: called without an identity change, the generation bump makes this
   * user's own PUT on the wire look like an outgoing user's, and the next pick goes out beside it
   * (MR !222 review, round 7).
   */
  private forgetCurrent(): void {
    // Read BEFORE the generation moves, which takes the outgoing user's PUT out of line.
    const switchInLine = !!this.lastChangeInLine();
    this.currentEpoch++;
    this.identityGeneration++;
    this.currentInFlight = undefined;
    // A change still on the wire went out under the OUTGOING user's bearer, so it moves their
    // current warehouse, not the incoming user's: a read for the new identity has nothing to wait
    // for, and waiting cost it up to CURRENT_READ_WAIT_MS with no warehouse in the header. The
    // generation bump above is what tells `lastChangeInLine` so. Its own
    // answer is already superseded by the epoch bump above, and a change still QUEUED behind it is
    // dropped here, never sent under the incoming user's bearer (MR !217 review).
    const queued = this.queuedChange;
    this.queuedChange = undefined;
    // The outgoing user's confirmed change is not the incoming user's to announce.
    this.unannounced = undefined;
    queued?.result.complete();
    queued?.done.complete();
    if (switchInLine) {
      this.switchEndedSub.next('dropped');
    }
    this.syncSwitchPending();
    this.warehouseChangeSub.next(undefined);
  }

  /** Whether an answer stamped `epoch` still describes the warehouse in force. See `currentEpoch`. */
  private isCurrent(epoch: number): boolean {
    return epoch === this.currentEpoch;
  }
}
